/**
 * Token Estimator — replaceable interface for the composer's token count meter.
 *
 * Wave 2C (chat-ui-improvements): Q4 estimator LOCKED for v1.
 *
 * ## Design decision
 * The spec (CHAT-UI-IMPROVEMENT-DESIGN.md §3.4 Q4) locks `Math.ceil(text.length / 4)`
 * as the v1 estimator. This is a well-known rule of thumb: roughly 4 characters
 * per token for English text in most BPE tokenizers (GPT-2, LLaMA, Qwen families).
 * Accuracy is ±20% for typical prose.
 *
 * ## Upgrade path
 * The `TokenEstimator` interface is the stable contract. When tiktoken WASM ships,
 * swap the `defaultTokenEstimator` export for the per-model implementation while
 * keeping all call-sites unchanged.
 *
 * ```ts
 * // future replacement — no call-site changes needed:
 * import { tiktokenEstimator } from './token-estimator-tiktoken';
 * export const defaultTokenEstimator: TokenEstimator = tiktokenEstimator;
 * ```
 */

// ── Interface ────────────────────────────────────────────────────────────────

/**
 * Stable contract for token count estimation in the composer.
 *
 * - `text`    — the raw string to estimate. Must not throw on empty string.
 * - `modelId` — optional model identifier. The v1 chars/4 estimator ignores
 *               this; a future tiktoken implementation uses it to select the
 *               correct vocabulary (cl100k_base for GPT-4, qwen2 tokenizer, …).
 *
 * Returns a non-negative integer.
 */
export interface TokenEstimator {
  estimate(text: string, modelId?: string): number;
}

// ── v1 — chars/4 (Q4 locked) ─────────────────────────────────────────────────

/**
 * Simple Q4 estimator: `Math.ceil(text.length / 4)`.
 *
 * Locked for v1 per spec §3.4 decision. Replace this export when tiktoken
 * WASM support ships (see upgrade path above).
 */
export const defaultTokenEstimator: TokenEstimator = {
  estimate(text: string): number {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
  },
};

// ── Meter thresholds ──────────────────────────────────────────────────────────

/** Default context window token budget shown in the meter (8k tokens). */
export const DEFAULT_TOKEN_LIMIT = 8_000;

/**
 * Threshold fractions for the color shift in the token count meter.
 * - amber: at or above 75 % utilization
 * - red:   at or above 90 % utilization
 */
export const TOKEN_METER_THRESHOLDS = {
  amber: 0.75,
  red: 0.90,
} as const;

/**
 * Derive the color tier given a utilization ratio (0–1).
 * Used by `TokenCountMeter` and its tests.
 */
export function tokenMeterColorTier(ratio: number): 'normal' | 'amber' | 'red' {
  if (ratio >= TOKEN_METER_THRESHOLDS.red) return 'red';
  if (ratio >= TOKEN_METER_THRESHOLDS.amber) return 'amber';
  return 'normal';
}
